import React, { memo, useCallback, useMemo } from 'react';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { Text, TextType } from 'ui-components/components/text';

import { buildUomHierarchy } from '../../../../../constants/units-of-measure';
import { emptyArray } from 'ui-components/default-props/empty';
import { areProductPropsEqual } from '../utils';
import styles from './uom-column.module.less';

type Props = import('../../../types').ColumnProps;
type DropdownOption = {
  id?: number;
  value?: string;
  label?: string;
  eaches?: number;
  parentId?: number | null;
  conversionFactor?: number;
  [key: string]: unknown;
};

const renderSelectedValue = (option: DropdownOption) => <Text element='span' textType={TextType.Paragraph} value={option.label ?? ''} />;

const Column = ({ value }: Props) => {
  const { product, updateProductUom } = value;
  const { unitsOfMeasure = emptyArray, uomId, productId, rowKey, uomTypeName } = product;
  const dropdownId = `uom-select-${rowKey ?? productId}`;

  const options = useMemo(
    () =>
      [...unitsOfMeasure]
        .sort((a, b) => (a.eaches ?? 1) - (b.eaches ?? 1))
        .map((u) => ({
          id: u.productUnitOfMeasureId,
          label: u.uomTypeName,
          value: String(u.productUnitOfMeasureId),
          eaches: u.productUnitOfMeasureId === 0 ? undefined : u.eaches,
          parentId: u.productUnitOfMeasureId === 0 ? null : (u.parentProductUnitOfMeasureId ?? null),
          conversionFactor: u.productUnitOfMeasureId === 0 ? undefined : u.conversionFactor,
        })),
    [unitsOfMeasure],
  );

  const hierarchyItems = useMemo(
    () => options.map((o) => ({ id: o.id, label: o.label ?? '', eaches: o.eaches ?? 1, parentId: o.parentId ?? null })),
    [options],
  );

  const renderOption = useCallback(
    (option: DropdownOption) => {
      const label = buildUomHierarchy(
        { id: option.id ?? 0, label: option.label ?? '', eaches: option.eaches ?? 1, parentId: option.parentId ?? null },
        hierarchyItems,
      );
      return <Text className={styles['option-label']} element='span' textType={TextType.Paragraph} value={label} />;
    },
    [hierarchyItems],
  );

  const handleSelect = useCallback(
    (selectedValue: string) => {
      const selected = unitsOfMeasure.find((u) => String(u.productUnitOfMeasureId) === selectedValue);
      if (!selected) return;
      updateProductUom(
        rowKey ?? String(productId),
        selected.productUnitOfMeasureId,
        selected.uomTypeName,
        selected.conversionFactor,
        selected.eaches,
      );
    },
    [unitsOfMeasure, updateProductUom, rowKey, productId],
  );

  if (!unitsOfMeasure.length) {
    if (!uomTypeName) {
      return null;
    }
    return <Text className={styles['uom-pill']} element='span' textType={TextType.Caption} value={uomTypeName} />;
  }

  return (
    <SearchableDropdownField
      id={dropdownId}
      isMenuFullWidth
      onItemSelected={handleSelect}
      options={options}
      renderOption={renderOption}
      renderSelectedValue={renderSelectedValue}
      required
      selectedItemValue={uomId != null ? String(uomId) : (options[0]?.value ?? '')}
    />
  );
};

export const UomColumn = memo(Column, areProductPropsEqual('uomId'));
